const PictProvider = require('pict-provider');

class MediaRegistryProvider extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		this.OrphanObservationList = [];
		this.DefectObservationMap = {};
		this.BridgeElementSectionMap = {};
		this.BridgeElementSectionList = [];
		// Document observations load once per page; explicit refreshes re-read them.
		this._documentObservationsLoaded = false;
		/** @type {Promise<void>|null} the in-flight server refresh, shared by concurrent callers */
		this._documentObservationsRefresh = null;

		/**
		* @type {import('pict') & import('fable') &
		* {
		*   HeadlightRestClient: import('headlight-rest-client'),
		* }}
		*/
		this.pict;
		/** @type {import('log')} */
		this.pict.log;
		/** @type {import('fable')} */
		this.fable;
		/** @type {any} */
		this.options;
		/** @type {any} */
		this.AppData;

		this.AppData.MediaRegistryStore = this.AppData.DocumentData?.ReportData?.FormData?.MediaRegistryStore || { NamespaceObservationSetMap: {}, Namespaces: {} };
	}

	/**
	 * Updates the AppData Registry to match the FormData Registry.
	 */
	updateRegistryFromFormData()
	{
		this.AppData.MediaRegistryStore = this.AppData.DocumentData?.ReportData?.FormData?.MediaRegistryStore || { NamespaceObservationSetMap: {}, Namespaces: {} };
	}

	/**
	 * Updates the FormData Registry to match the AppData Registry.
	 */
	_updateFormDataRegistry()
	{
		if (!this.AppData.DocumentData?.ReportData?.FormData)
		{
			return;
		}
		this.AppData.DocumentData.ReportData.FormData.MediaRegistryStore = this.AppData.MediaRegistryStore;
	}

	/**
	 * Adds a set of observations to the media registry. Preserves any existing ordinals.
	 * @param {String} namespace - namespace of the observations.
	 * @param {Array} observations - the observations to register.
	 */
	registerMediaSet(namespace, observations)
	{
		if (!namespace)
		{
			this.pict.log.error('No namespace was provided to media registry provider, returning early.');
			return;
		}
		if (!observations)
		{
			this.pict.log.error('No observations provided to media registry provider, returning early.');
			return;
		}

		// If the namespace is not already in the registry, add it.
		if (!this.pict.AppData.MediaRegistryStore.Namespaces?.[namespace])
		{
			this.pict.AppData.MediaRegistryStore.Namespaces[namespace] = {};
		}

		for (const o of observations)
		{
			if (!this.pict.AppData.MediaRegistryStore.Namespaces[namespace][o.IDObservation])
			{
				// Add an entry for the observation under the correct namespace.
				this.pict.AppData.MediaRegistryStore.Namespaces[namespace][o.IDObservation] =
				{
					IDObservation: o.IDObservation,
					ObservationType: o.ObservationType,
					CreateDate: o.CreateDate,
				};
			}
			else if (o.Ordinal)
			{
				this.pict.AppData.MediaRegistryStore.Namespaces[namespace][o.IDObservation].Ordinal = o.Ordinal;
			}
		}

		// Clear out any "old" keys that should no longer be present in the set.
		const newIDs = observations.map((o) => o.IDObservation);
		for (const id of Object.keys(this.pict.AppData.MediaRegistryStore.Namespaces[namespace]))
		{
			if (!newIDs.includes(parseInt(id)))
			{
				delete this.pict.AppData.MediaRegistryStore.Namespaces[namespace][id];
			}
		}

		// Update the ordinals so that any observation without an ordinal gets one.
		this._updateOrdinalsForNamespace(namespace);

		// Save the changes to the form data.
		this._updateFormDataRegistry();

		return this._getFullConstructedRegistrySetFromArray(namespace, observations);
	}

	/**
	 * Adds a set of observation ids to the media registry. Preserves any existing ordinals.
	 * @param {String} namespace - namespace of the observations.
	 * @param {Array} observationIDs - the IDs of the observations to register.
	 */
	async registerMediaSetByIDList(namespace, observationIDs)
	{
		if (!namespace)
		{
			this.pict.log.error('No namespace was provided to media registry provider, returning early.');
			return;
		}
		if (!observationIDs)
		{
			this.pict.log.error('No observations provided to media registry provider, returning early.');
			return;
		}
		try
		{
			return this.registerMediaSet(namespace, await this.pict.providers.ObservationProvider.getObservationByIDList(observationIDs));
		}
		catch (err)
		{
			this.pict.log.error('Error fetching Observations: ', err.message);
		}
	}

	/**
	 * Adds an observation set to the media registry using its id to look up the observations. Preserves any existing ordinals.
	 * @param {String} namespace - namespace of the observations.
	 * @param {Number} IDObservationSet - the ID of the observation set to register.
	 */
	async registerMediaSetByIDObservationSet(namespace, IDObservationSet)
	{
		if (!namespace)
		{
			this.pict.log.error('No namespace was provided to media registry provider, returning early.');
			return;
		}
		if (!IDObservationSet)
		{
			this.pict.log.error('No IDObservationSet provided to media registry provider, clearing namespace and returning early.');
			this.clearNamespace(namespace);
			return;
		}
		try
		{
			this.pict.AppData.MediaRegistryStore.NamespaceObservationSetMap[namespace] = IDObservationSet;
			return this.registerMediaSet(namespace, await this.pict.providers.ObservationSetProvider.loadSetObservations(IDObservationSet));
		}
		catch (err)
		{
			this.pict.log.error('Error fetching Observations: ', err.message);
		}
	}

	/**
	 * Updates the registry copies of any namespaces attached to a specific observation set id.
	 * @param {Number} IDObservationSet - The id of the observation set we are updating namespaces for.
	 * @param {Array} [observations] - Optional, can pass a set of observations if we already have them to prevent needing to look them up with a network call.
	 */
	async updateNamespacesByIDObservationSet(IDObservationSet, observations)
	{
		if (!IDObservationSet)
		{
			this.pict.log.error('No IDObservationSet provided to media registry provider, returning early.');
			return;
		}
		let observationList = observations || [];
		if (!observations)
		{
			try
			{
				observationList = await this.pict.providers.ObservationSetProvider.loadSetObservations(IDObservationSet);
			}
			catch (err)
			{
				this.pict.log('Error fetching observation set: ', err.message);
				return;
			}
		}
		for (const namespace of Object.keys(this.pict.AppData.MediaRegistryStore.NamespaceObservationSetMap))
		{
			if (this.pict.AppData.MediaRegistryStore.NamespaceObservationSetMap[namespace] == IDObservationSet)
			{
				return this.registerMediaSet(namespace, observationList);
			}
		}
	}

	/**
	 * Clears any registry data from this namespace.
	 * @param {String} namespace - namespace we are clearing out.
	 */
	async clearNamespace(namespace)
	{
		if (this.pict.AppData.MediaRegistryStore.Namespaces[namespace])
		{
			this.pict.AppData.MediaRegistryStore.Namespaces[namespace] = {};
		}
		if (this.pict.AppData.MediaRegistryStore.NamespaceObservationSetMap[namespace])
		{
			delete this.pict.AppData.MediaRegistryStore.NamespaceObservationSetMap[namespace];
		}
	}

	/**
	 * Adds a new single observation to a registry under a namespace.
	 * @param {String} namespace - namespace of the observation.
	 * @param {Object} observation - the observation to add to the namespace.
	 *
	 * @returns {Object} The registry entry that was created, or the existing one if it is already present.
	 */
	_addRegistryEntry(namespace, observation)
	{
		// If this observation is already registered, return the existing entry.
		if (this.pict.AppData.MediaRegistryStore.Namespaces?.[namespace]?.[observation.IDObservation])
		{
			return this.pict.AppData.MediaRegistryStore.Namespaces?.[namespace]?.[observation.IDObservation];
		}
		// If the namespace is not already in the registry, add it.
		if (!this.pict.AppData.MediaRegistryStore.Namespaces?.[namespace])
		{
			this.pict.AppData.MediaRegistryStore.Namespaces[namespace] = {};
		}

		// Add an entry for the observation under the correct namespace.
		this.pict.AppData.MediaRegistryStore.Namespaces[namespace][observation.IDObservation] =
		{
			IDObservation: observation.IDObservation,
			ObservationType: observation.ObservationType,
			CreateDate: observation.CreateDate,
		};

		// Update the ordinals so that any observation without an ordinal gets one.
		this._updateOrdinalsForNamespace(namespace);

		// Save the change to the form data.
		this._updateFormDataRegistry();

		return this.pict.AppData.MediaRegistryStore.Namespaces[namespace][observation.IDObservation];
	}

	/**
	 * Adds a new single observation to a registry under a namespace.
	 * @param {String} namespace  - namespace of the observation.
	 * @param {Number} IDObservation - the ID of the observation being added.
	 *
	 * @returns {Promise<Object>} The registry entry that was created, or the existing one if it is already present.
	 */
	async _addRegistryEntryByID(namespace, IDObservation)
	{
		try
		{
			return this._addRegistryEntry(namespace, await this.pict.providers.ObservationProvider.getObservationByID(IDObservation));
		}
		catch (err)
		{
			this.pict.log.error('Error fetching observation: ', err.message);
			return {};
		}
	}

	/**
	 * Updates the ordinals of any observations within a namespace with no ordinals (preserves existing ordinals).
	 * @param {String} namespace - namespace of the observations.
	 */
	_updateOrdinalsForNamespace(namespace)
	{
		// Get the observation array presorted by Ordinal and CreateDate.
		const observationArray = this._getConstructedRegistrySet(namespace);
		const maxOrdinalsByType = {};
		for (const o of observationArray)
		{
			if (!o.Ordinal)
			{
				if (!maxOrdinalsByType[o.ObservationType])
				{
					o.Ordinal = 1;
				}
				else
				{
					o.Ordinal = maxOrdinalsByType[o.ObservationType] + 1;
				}
				this.pict.AppData.MediaRegistryStore.Namespaces[namespace][o.IDObservation].Ordinal = o.Ordinal;
			}
			maxOrdinalsByType[o.ObservationType] = o.Ordinal;
		}
	}

	/**
	 * Turns the object structured in the registry for a namespace into a sorted array and returns it.
	 * @param {String} namespace - namespace of the observations.
	 *
	 * @returns {Array} The sorted array of Observations.
	 */
	_getConstructedRegistrySet(namespace)
	{
		if (!this.pict.AppData.MediaRegistryStore.Namespaces[namespace])
		{
			this.pict.AppData.MediaRegistryStore.Namespaces[namespace] = {};
			return [];
		}
		const observationArr = [];
		for (const id of Object.keys(this.pict.AppData.MediaRegistryStore.Namespaces[namespace]))
		{
			observationArr.push(this.pict.AppData.MediaRegistryStore.Namespaces[namespace][id]);
		}
		return observationArr.sort((a, b) =>
		{
			// Sort first based on ordinal.
			if (a.Ordinal < b.Ordinal)
			{
				return -1;
			}
			if (b.Ordinal < a.Ordinal)
			{
				return 1;
			}
			// If one doesn't have an ordinal, prefer the one that does.
			if (a.Ordinal && !b.Ordinal)
			{
				return -1;
			}
			if (!a.Ordinal && b.Ordinal)
			{
				return 1;
			}
			// Sort whatever's left based on date.
			return new Date(a.CreateDate).getTime() - new Date(b.CreateDate).getTime();
		});
	}

	/**
	 * Turns the object structured in the registry for a namespace into a sorted array and returns it. Also fetches the full observations and decorates them with the registry values.
	 * This is the non async version that can be called when you already have the observations array.
	 * @param {String} namespace - namespace of the observations.
	 * @param {Array} observations - if we already have the observations, we don't need to fetch them again so they are passed into this method.
	 *
	 * @returns {Array} The sorted array of Observations.
	 */
	_getFullConstructedRegistrySetFromArray(namespace, observations)
	{
		if (!this.pict.AppData.MediaRegistryStore.Namespaces[namespace])
		{
			this.pict.AppData.MediaRegistryStore.Namespaces[namespace] = {};
			return [];
		}
		const observationArr = [];
		const fullObservations = observations;
		if (!fullObservations)
		{
			this.pict.log.error('No Observations fetched as yet, returning just what is in registry.');
			for (const id of Object.keys(this.pict.AppData.MediaRegistryStore.Namespaces[namespace]))
			{
				observationArr.push(this.pict.AppData.MediaRegistryStore.Namespaces[namespace][id]);
			}
		}
		else
		{
			for (const o of fullObservations)
			{
				o.Ordinal = this.pict.AppData.MediaRegistryStore.Namespaces[namespace][o.IDObservation].Ordinal;
				observationArr.push(o);
			}
		}

		return observationArr.sort((a, b) =>
		{
			// Sort first based on ordinal.
			if (a.Ordinal < b.Ordinal)
			{
				return -1;
			}
			if (b.Ordinal < a.Ordinal)
			{
				return 1;
			}
			// If one doesn't have an ordinal, prefer the one that does.
			if (a.Ordinal && !b.Ordinal)
			{
				return -1;
			}
			if (!a.Ordinal && b.Ordinal)
			{
				return 1;
			}
			// Sort whatever's left based on date.
			return new Date(a.CreateDate).getTime() - new Date(b.CreateDate).getTime();
		});
	}

	/**
	 * Turns the object structured in the registry for a namespace into a sorted array and returns it. Also fetches the full observations and decorates them with the registry values.
	 * @param {String} namespace - namespace of the observations.
	 *
	 * @returns {Promise<Array>} The sorted array of Observations.
	 */
	async _getFullConstructedRegistrySet(namespace)
	{
		if (!this.pict.AppData.MediaRegistryStore.Namespaces[namespace])
		{
			this.pict.AppData.MediaRegistryStore.Namespaces[namespace] = {};
			return [];
		}
		const observationArr = [];
		try
		{
			const fullObservations = await this.pict.providers.ObservationProvider.getObservationByIDList(Object.keys(this.pict.AppData.MediaRegistryStore.Namespaces[namespace]) || []);
			for (const o of fullObservations)
			{
				o.Ordinal = this.pict.AppData.MediaRegistryStore.Namespaces[namespace][o.IDObservation].Ordinal;
				observationArr.push(o);
			}
		}
		catch (err)
		{
			this.pict.log.error('Error fetching full observations, returning just what is in registry.', err.message);
			for (const id of Object.keys(this.pict.AppData.MediaRegistryStore.Namespaces[namespace]))
			{
				observationArr.push(this.pict.AppData.MediaRegistryStore.Namespaces[namespace][id]);
			}
		}

		return observationArr.sort((a, b) =>
		{
			// Sort first based on ordinal.
			if (a.Ordinal < b.Ordinal)
			{
				return -1;
			}
			if (b.Ordinal < a.Ordinal)
			{
				return 1;
			}
			// If one doesn't have an ordinal, prefer the one that does.
			if (a.Ordinal && !b.Ordinal)
			{
				return -1;
			}
			if (!a.Ordinal && b.Ordinal)
			{
				return 1;
			}
			// Sort whatever's left based on date.
			return new Date(a.CreateDate).getTime() - new Date(b.CreateDate).getTime();
		});
	}

	/**
	 * Fetches an entry from the media registry using its namespace and id.
	 * @param {String} namespace - namespace to look for the observation in.
	 * @param {Number} IDObservation - the observation we are looking up.
	 *
	 * @returns {Object} The matching registry entry.
	 */
	_getRegistryEntry(namespace, IDObservation)
	{
		return this.pict.AppData.MediaRegistryStore.Namespaces?.[namespace]?.[IDObservation] || {};
	}

	/**
	 * Retrieves the ordinal that should be assigned to an observation within a given namespace. Will generate one if the registry entry does not exist.
	 * @param {String} namespace - namespace of the observation.
	 * @param {Number} IDObservation - the ID of the observation we are checking the ordinal of.
	 *
	 * @returns {Promise<Number>} The ordinal for the observation.
	 */
	async getOrdinal(namespace, IDObservation)
	{
		if (!namespace)
		{
			this.pict.log.error('No namespace was provided to media registry provider, returning early.');
			return;
		}
		const entry = this._getRegistryEntry(namespace, IDObservation);
		if (entry.Ordinal)
		{
			return entry.Ordinal;
		}
		const newEntry = await this._addRegistryEntryByID(namespace, IDObservation);
		return newEntry.Ordinal;
	}

	/**
	 * Returns the array of observations that are part of this set with the additional registry fields (like ordinal) decorated on them.
	 * @param {String} namespace - namespace of the observations.
	 *
	 * @returns {Promise<Array>} The array of observations that are part of this set with the additional registry fields (like ordinal) decorated on them.
	 */
	async getMediaSet(namespace)
	{
		if (!namespace)
		{
			this.pict.log.error('No namespace was provided to media registry provider, returning early.');
			return;
		}

		return await this._getFullConstructedRegistrySet(namespace);
	}

	/**
	 * Returns the id of the observation set attached to a namespace (if one exists).
	 * @param {String} namespace - namespace of the observations.
	 *
	 * @returns {Number} The IDObservationSet for the given namespace.
	 */
	getObservationSetIDByNamespace(namespace)
	{
		if (!namespace)
		{
			this.pict.log.error('No namespace was provided to media registry provider, returning early.');
			return;
		}
		return this.pict.AppData.MediaRegistryStore.NamespaceObservationSetMap[namespace];
	}

	/**
	 * Returns the array of observations in the observation set attached to a namespace (if one exists). It will be decorated with fields from the registry and be sorted by ordinal.
	 * @param {String} namespace - namespace of the observations.
	 *
	 * @returns {Promise<Array>} The full Observation Set for the given namespace.
	 */
	async getObservationSetByNamespace(namespace)
	{
		if (!namespace)
		{
			this.pict.log.error('No namespace was provided to media registry provider, returning early.');
			return;
		}
		const IDObservationSet = this.pict.AppData.MediaRegistryStore.NamespaceObservationSetMap[namespace];
		if (!IDObservationSet)
		{
			this.pict.log.warn('No ObservationSet is registered to this namespace.');
			return [];
		}
		await this.registerMediaSetByIDObservationSet(namespace, IDObservationSet);
		return await this.getMediaSet(namespace);
	}

	/**
	 * Method for refreshing the registry namespace for the 'global' set of photos on a document.
	 * @param {Array} [pObservations] - optional, if we already have the observations we can pass them in to avoid an unnecessary network call to fetch them again (or to avoid using a cached set)
	 */
	async refreshDocumentObservations(pObservations)
	{
		if (pObservations)
		{
			// When observations are passed directly (as full objects), register them
			// without re-fetching from the API — they are already the latest state.
			this.registerMediaSet('DocumentObservations', pObservations);
			this._documentObservationsLoaded = true;
			return;
		}
		// Single-flight: several card decks marshaling at once share one document read.
		if (this._documentObservationsRefresh)
		{
			return this._documentObservationsRefresh;
		}
		this._documentObservationsRefresh = (async () =>
		{
			try
			{
				const documentRecord = await this.pict.providers.DocumentProvider.getDocument(this.AppData.DocumentData.ReportData.IDDocument);
				await this.registerMediaSetByIDList('DocumentObservations', documentRecord.Observations);
			}
			catch (err)
			{
				this.pict.log.error('Error fetching document record, will default to observations listed on the local document: ', err.message);
				this.registerMediaSet('DocumentObservations', this.AppData.DocumentData.ReportData.Observations);
			}
			this._documentObservationsLoaded = true;
		})().finally(() =>
		{
			this._documentObservationsRefresh = null;
		});
		return this._documentObservationsRefresh;
	}

	/**
	 * Load the document's observation set once. The loaded document already carries the full
	 * observation objects, so no request is needed; the server is only read when the document
	 * holds bare ids. Call refreshDocumentObservations to re-read after a change.
	 * @return {Promise<void>}
	 */
	primeDocumentObservations()
	{
		if (this._documentObservationsLoaded)
		{
			return Promise.resolve();
		}
		const tmpLocalObservations = this.AppData.DocumentData?.ReportData?.Observations;
		if (Array.isArray(tmpLocalObservations) && tmpLocalObservations.every((o) => o && (typeof o === 'object') && ('IDObservation' in o)))
		{
			return this.refreshDocumentObservations(tmpLocalObservations);
		}
		return this.refreshDocumentObservations();
	}

	/**
	 * Method that returns the 'global' set of photos on a document decorated with any fields from the registry.
	 * Loads the set on first use; mutation paths refresh it explicitly.
	 */
	async getDocumentObservations()
	{
		await this.primeDocumentObservations();
		return this.getMediaSet('DocumentObservations');
	}

	/**
	 * Method that returns the 'global' set of photos on a document decorated with any fields from the registry, excluding any that are already included in any of the namespaces attached to observation sets (like card decks that are using observation sets.)
	 * This is useful for when you want to show the user all of the photos on a document that are not already being used in a specific way on the form.
	 * @param {Array<string>} [pNamespacesToExclude] - Optional array of specific namespaces to exclude from. When provided, only observations in these namespaces are excluded. When omitted, all namespaces in NamespaceObservationSetMap are used.
	 */
	async getDocumentObservationsExcludingCurrentSets(pNamespacesToExclude)
	{
		await this.primeDocumentObservations();
		const documentObservations = await this.getMediaSet('DocumentObservations');
		const currentSetObservationIDs = new Set();
		const namespacesToCheck = pNamespacesToExclude || Object.keys(this.pict.AppData.MediaRegistryStore.NamespaceObservationSetMap);
		for (const namespace of namespacesToCheck)
		{
			const setObservations = await this.getMediaSet(namespace);
			for (const o of setObservations)
			{
				currentSetObservationIDs.add(o.IDObservation);
			}
		}
		return documentObservations.filter((o) => !currentSetObservationIDs.has(o.IDObservation));
	}

	/**
	 * Returns the array of observations that are part of this set with just the decorated registry fields (does not fetch the full observations.)
	 * @param {String} namespace - namespace of the observations.
	 * @returns {Array} The array of observations that are part of this set with only the registry fields (like ordinal) on them.
	*/
	getMediaSetPartial(namespace)
	{
		if (!namespace)
		{
			this.pict.log.error('No namespace was provided to media registry provider, returning early.');
			return;
		}
		return this._getConstructedRegistrySet(namespace);
	}
}

module.exports = MediaRegistryProvider;
